import React, { useEffect, useState, useRef, useCallback, useMemo, startTransition } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import stringToClassName from 'commons/stringToClassName';
import { transformInspirationImageUrl } from 'components/consumer/inspiration-photos-grid/search-state/utils';
import { searchClient } from 'components/essentials/AlgoliaSearchClient';
import { ALGOLIA_INDEX_NAME } from 'commons/constants';
import avo from 'analytics/avo';
import IconHeart2 from 'components/icons/iconHeart2';
import IconHeart4 from 'components/icons/iconHeart4';
import useFabricFavs from 'data-hooks/useFabricFavs';
import IconStarFull from 'components/icons/iconStarFull';
import JBImage from 'components/essentials/JBImage';
import SidebarModal from 'components/consumer/SidebarModal';
import FabricAdditionalAttributes from 'components/consumer/FabricAdditionalAttributes';
import CtaLink from 'components/essentials/Cta/CtaLink';
import PDPOrderSwatchesSidebar from 'components/consumer/PDP/PDPOrderSwatchesSidebar';
import useScreen from 'hooks/useScreen';
import IconClose from 'components/icons/iconClose';
import withBrowserOnly from 'components/hoc/withBrowserOnly';
import SanitizedHtml from 'components/consumer/SanitizedHtml';
import TabPanel from 'components/consumer/TabPanel';
import MixedGrid from 'components/consumer/mixed-grid';
import ProductsGridStatic from 'components/consumer/products-grid/ProductsGridStatic';
import useStaticProductCardData from 'hooks/useStaticProductCardData';
import FabricGallery from 'components/consumer/GridWithSidebar/components/FabricGallery';
const SwatchesFabricDetailSidebarUI = ({
  isOpen,
  onClose,
  selectedFabric,
  galleryType,
  mobileHideHeader = false,
  reviews = [],
  rating = 0
}) => {
  const history = useHistory();
  const [showAttr, setShowAttr] = useState(false);
  const [currentSelection, setCurrentSelection] = useState('all');
  const [hoveredValue, setHoveredValue] = useState({
    isHovered: false,
    hoveredOver: null
  });
  const [, setModalValue] = useState({
    isClicked: false,
    modalFor: null
  });
  const {
    isMobile,
    isMobileOrTablet,
    isSmallScreen
  } = useScreen();
  const [isOrderSwatchesSidebarOpen, setIsOrderSwatchesSidebarOpen] = useState(false);
  const [productIds, setProductIds] = useState([]);
  const [photosList, setPhotosList] = useState([]);
  const [allDataList, setAllDataList] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasLoadMoreReviews, setHasLoadMoreReviews] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const onCloseClick = useCallback(() => {
    if (isMobile) history.goBack();
    onClose();
  }, [history, isMobile, onClose]);
  const {
    actions: {
      setOptionAsFav,
      removeOptionAsFav
    } = {},
    data: {
      userFabricFavs: favFabricsObj
    } = {}
  } = useFabricFavs();
  const location = 'Swatches';
  const favProps = useMemo(() => ({
    fabricId: selectedFabric?.option_value_id,
    fabricName: selectedFabric?.option_value,
    fabricColor: selectedFabric?.option_color,
    location
  }), [location, selectedFabric]);
  const isFavorited = !!favFabricsObj?.[selectedFabric?.option_value_id];
  const toggleFavClick = favParams => {
    if (favFabricsObj?.[favParams.fabricId] && removeOptionAsFav) {
      removeOptionAsFav(favParams.fabricId);
      avo.fabricUnfavorited(favParams);
    } else if (!favFabricsObj?.[favParams.fabricId] && setOptionAsFav) {
      setOptionAsFav(favParams.fabricId);
      avo.fabricFavorited(favParams);
    }
  };
  const mergeList = useCallback((productsList, photosListParam, reviewsList) => {
    const list = [];
    let i = 0;
    let j = 0;
    while (productsList[i] || photosListParam[j]) {
      if (productsList[i]) {
        list.push(productsList[i]);
        i += 1;
      }
      if (photosListParam[j]) {
        list.push(photosListParam[j]);
        j += 1;
      }
    }
    list.push(...reviewsList);
    return list;
  }, []);
  const queryAlgolia = useCallback(async (page = 0) => {
    const fabricName = selectedFabric?.option_value;
    if (!fabricName) {
      return;
    }
    if (page > 0) {
      setLoadingMore(true);
    }
    const maxProducts = 6;
    const updatedAlgoliaQuery = [{
      indexName: ALGOLIA_INDEX_NAME.PRODUCTS,
      query: '',
      params: {
        hitsPerPage: maxProducts,
        filters: `option_value:"${fabricName}"`,
        numericFilters: ['item_availability=0', 'visibility>0', 'visibility<4'],
        page: 0
      }
    }, {
      indexName: 'customer_photos',
      query: '',
      params: {
        hitsPerPage: 6,
        filters: `products.upholstery:"${fabricName}"`,
        page
      }
    }];
    try {
      const response = await searchClient.search(updatedAlgoliaQuery);
      const {
        results
      } = response;
      const productResults = results[0].hits.length ? results[0].hits : [];
      const newProductIds = productResults.map(hit => parseInt(hit.product_id, 10));
      let photoCardsList = results[1].hits.length ? results[1].hits : [];
      photoCardsList = photoCardsList.map(photo => ({
        _sort: parseInt(photo.photo_id, 10) % 1000,
        card_type: 'customer_photo',
        ...photo,
        image_link: transformInspirationImageUrl(photo.image_link)
      }));
      const reviewsList = reviews ? reviews.slice(page * 6, (page + 1) * 6).map(review => ({
        card_type: 'review',
        ...review
      })) : [];
      const limitedNewProductIds = page === 0 ? newProductIds.slice(0, 6) : [];
      const productIdsDataList = limitedNewProductIds.map(id => ({
        id,
        _sort: id % 1000,
        card_type: 'product'
      }));
      const newAllDataList = mergeList(productIdsDataList, photoCardsList, reviewsList);
      if (page === 0) {
        // Use startTransition to mark data updates as non-urgent
        // This prevents blocking the UI during data loading
        startTransition(() => {
          setProductIds(limitedNewProductIds);
          setPhotosList(photoCardsList);
          setAllDataList(newAllDataList);
        });
      }
      setHasLoadMoreReviews(reviews && (page + 1) * 6 < reviews.length);
      setCurrentPage(page);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error querying Algolia:', error);
    } finally {
      if (page > 0) {
        setLoadingMore(false);
      }
    }
  }, [selectedFabric, reviews, mergeList]);
  const loadedFabricRef = useRef(null);
  const reviewsAddedRef = useRef(false);
  useEffect(() => {
    const fabricKey = selectedFabric?.option_value;
    if (fabricKey && loadedFabricRef.current !== fabricKey) {
      setCurrentPage(0);
      setCurrentSelection('all');
      reviewsAddedRef.current = false;
      loadedFabricRef.current = fabricKey;
      queryAlgolia(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFabric?.option_value]);
  useEffect(() => {
    const fabricKey = selectedFabric?.option_value;
    if (reviews && reviews.length > 0 && allDataList.length > 0 && !reviewsAddedRef.current && loadedFabricRef.current === fabricKey) {
      const hasReviews = allDataList.some(item => item.card_type === 'review');
      if (!hasReviews) {
        const reviewsList = reviews.slice(0, 6).map(review => ({
          card_type: 'review',
          ...review
        }));
        setAllDataList(prev => {
          const withoutReviews = prev.filter(item => item.card_type !== 'review');
          reviewsAddedRef.current = true;
          return [...withoutReviews, ...reviewsList];
        });
      } else {
        reviewsAddedRef.current = true;
      }
    }
  }, [reviews, allDataList, selectedFabric?.option_value]);
  const limitedProductIds = useMemo(() => productIds.slice(0, 6), [productIds]);
  const {
    loading: allStaticProductDataLoading,
    products: allStaticProductData
  } = useStaticProductCardData({
    productIds: limitedProductIds,
    limit: 6
  });
  const limitedProducts = useMemo(() => (allStaticProductData || []).slice(0, 6), [allStaticProductData]);
  const showAttrTimeoutRef = useRef(null);
  useEffect(() => {
    if (showAttrTimeoutRef.current) {
      clearTimeout(showAttrTimeoutRef.current);
    }
    if (isOpen) {
      showAttrTimeoutRef.current = setTimeout(() => {
        setShowAttr(true);
        showAttrTimeoutRef.current = null;
      }, 600);
    } else {
      setShowAttr(false);
    }
    return () => {
      if (showAttrTimeoutRef.current) {
        clearTimeout(showAttrTimeoutRef.current);
      }
    };
  }, [isOpen]);
  const imgixProps = useMemo(() => ({
    height: 300,
    width: !isMobile ? 600 : undefined,
    className: isMobile ? 'h-full w-full object-cover' : ''
  }), [isMobile]);
  const loadMore = useCallback(() => {
    queryAlgolia(currentPage + 1);
  }, [currentPage, queryAlgolia]);
  const toggleReviewOnHover = useCallback((isHovered, hoveredOver) => {
    setHoveredValue({
      isHovered,
      hoveredOver
    });
  }, []);
  const toggleModalView = useCallback((isClicked, modalFor) => {
    setModalValue({
      isClicked,
      modalFor
    });
  }, []);
  const handleTabSelect = useCallback(id => {
    setCurrentSelection(id);
  }, []);
  const reviewsListForTabs = useMemo(() => reviews?.map(review => ({
    card_type: 'review',
    ...review
  })) || [], [reviews]);
  const limitedAllDataList = useMemo(() => {
    if (!allDataList.length) return [];
    const products = allDataList.filter(item => item.card_type === 'product').slice(0, 6);
    const otherItems = allDataList.filter(item => item.card_type !== 'product');
    return [...products, ...otherItems];
  }, [allDataList]);
  const tabs = useMemo(() => {
    const tabsArray = [];
    if (!selectedFabric) {
      return tabsArray;
    }
    const tabsConfig = {
      all: true,
      products: true,
      customerPhotos: true,
      customerPhotosTitle: isSmallScreen ? 'Photos' : 'Customer Photos',
      fabricReviews: true,
      fabricReviewsTitle: isSmallScreen ? 'Reviews' : 'Fabric Reviews'
    };
    if (tabsConfig.all) {
      const label = 'All';
      const id = stringToClassName(label);
      tabsArray.push({
        label,
        id,
        contentId: `${id}-content`,
        content: <div className="mt-2 w-full">
            {reviews?.length && photosList.length ? <MixedGrid allData={limitedAllDataList} gridWithReviews={reviews?.length || 0} hoveredValue={hoveredValue} isMixedGridTab keyId="All MG" layout="minimal" loading={false} loadingMore={false} loadMore={null} maxCardsCount={6} onHoverHandler={toggleReviewOnHover} optionState="allOpts" toggleModalView={toggleModalView} allStaticProductData={limitedProducts} /> : <ProductsGridStatic columnsOnDesktop="2" columnsOnMobile="1" layout="minimal-with-attributes" listName="SideBar" loading={allStaticProductDataLoading} maxCardsCount={6} productsData={limitedProducts} />}
          </div>
      });
    }
    if (tabsConfig.products) {
      const productsTitle = 'Products';
      const id = stringToClassName(productsTitle);
      tabsArray.push({
        label: productsTitle,
        id,
        contentId: `${id}-content`,
        content: <div className="mt-5">
            <ProductsGridStatic columnsOnDesktop="2" columnsOnMobile="1" layout="minimal-with-attributes" listName="SideBar" loading={allStaticProductDataLoading} maxCardsCount={6} productsData={limitedProducts} />
          </div>
      });
    }
    if (tabsConfig.customerPhotos) {
      const label = tabsConfig.customerPhotosTitle || 'Customer Photos';
      const id = stringToClassName(label);
      tabsArray.push({
        label,
        id,
        contentId: `${id}-content`,
        tabClass: 'customer-photos-tab-label',
        content: <div className="grid gap-4 grid-cols-[repeat(auto-fit,minmax(200px,1fr))] mt-2 w-full">
            <MixedGrid allData={photosList} hoveredValue={hoveredValue} isMixedGridTab keyId="Customer Photos MG" loading={false} loadingMore={false} loadMore={null} maxCardsCount={6} onHoverHandler={toggleReviewOnHover} showProductOptions toggleModalView={toggleModalView} />
          </div>
      });
    }
    if (tabsConfig.fabricReviews) {
      const label = tabsConfig.fabricReviewsTitle || 'Fabric Reviews';
      const id = stringToClassName(label);
      tabsArray.push({
        label,
        id,
        contentId: `${id}-content`,
        tabClass: 'fabric-reviews-tab-label',
        content: <div className="m-4">
            <MixedGrid maxCardsCount={6} allData={reviewsListForTabs} loadMore={hasLoadMoreReviews ? loadMore : null} loadingMore={loadingMore} loading={false} gridWithOneCol keyId="Reviews MG" />
          </div>
      });
    }
    return tabsArray;
  }, [selectedFabric, reviews, photosList, limitedAllDataList, limitedProducts, reviewsListForTabs, allStaticProductDataLoading, hoveredValue, loadingMore, loadMore, toggleReviewOnHover, toggleModalView, hasLoadMoreReviews, isSmallScreen]);
  if (!selectedFabric) return null;
  return <>
      <SidebarModal isOpen={isOpen} onClose={onClose} mobileNoPadding desktopNoPadding desktopNoCloseIcon desktopWidthSize="lg" mobileHideHeader={mobileHideHeader} mobileIsFullHeight>
        <div className={classNames('relative bg-white', {
        '-top-5': !isMobileOrTablet && selectedFabric?.sliderImages
      })}>
          {/* Close button - visible on all screen sizes */}
          <button type="button" className={classNames('absolute top-4 right-4 z-10 !border-0 items-center bg-white border-solid border-gray-light6 rounded-[48px] cursor-pointer flex h-10 justify-center w-10 shadow-sm hover:shadow-md transition-shadow', {
          'top-7': !isMobileOrTablet && selectedFabric?.sliderImages
        })} onClick={onCloseClick} aria-label="Close fabric detail sidebar">
            <IconClose />
          </button>

          <div className="h-[300px]">
            {selectedFabric?.sliderImages && galleryType === 'FabricMultiImg' ? <FabricGallery slides={selectedFabric.sliderImages} isMobileMaterialSwatches bottomRightSide={<button type="button" className={classNames('![background:none] !border-0 !rounded-full !flex !min-h-[34px] !min-w-[34px] !mr-0', 'focus:!outline-0', 'focus-visible:outline-offset-8', 'hover:cursor-pointer', '[&_svg]:!h-8 [&_svg]:!w-8', '[&_svg_path]:!fill-white', '[&:hover_svg_path]:stroke-[#107c8c]', {
            '[&:hover_svg_path]:!fill-brand': !isFavorited
          })} onClick={e => {
            e.stopPropagation();
            toggleFavClick(favProps);
          }}>
                    {isFavorited ? <IconHeart2 fill="white" color="white" /> : <IconHeart4 />}
                  </button>} /> : <div className="h-full">
                <JBImage src={selectedFabric.fabricURL} fallbackSrc={selectedFabric.fabricFallbackURL}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...imgixProps} />
                <div className="absolute top-64 right-[5%] w-fit">
                  <button type="button" className={classNames('![background:none] !border-0 !rounded-full !flex !min-h-[34px] !min-w-[34px] !mr-0', 'focus:!outline-0', 'focus-visible:outline-offset-8', 'hover:cursor-pointer', '[&_svg]:!h-8 [&_svg]:!w-8', '[&_svg_path]:!fill-white', '[&:hover_svg_path]:stroke-[#107c8c]', {
                'relative bottom-5': isMobile,
                '[&:hover_svg_path]:!fill-brand': !isFavorited
              })} onClick={e => {
                e.stopPropagation();
                toggleFavClick(favProps);
              }}>
                    {isFavorited ? <IconHeart2 fill="white" color="white" /> : <IconHeart4 />}
                  </button>
                </div>
              </div>}
          </div>

          <div className="px-8 py-10 max-[767px]:p-4">
            <h2 className="text-2xl font-bold m-0">
              {selectedFabric.option_value}
              {selectedFabric.exclude_from_sale ? ' - Limited Edition' : ''}
            </h2>

            {reviews?.length > 0 && <div className="flex items-center [&_span]:flex [&_span]:items-center my-2 mx-0">
                <span>
                  <IconStarFull className="relative -top-px w-[11px] h-[11px] max-w-[inherit] mr-[.2rem] [&_path]:fill-yellow [&_path]:stroke-[none]" />
                </span>
                {rating > 0 && <>
                    <span className="text-xs">{rating}/5</span>&nbsp;
                  </>}
                <span className="text-xs">
                  ({reviews.length} review{reviews.length > 1 ? 's' : ''})
                </span>
              </div>}

            {showAttr && <FabricAdditionalAttributes selectedItemGroupMaterial={selectedFabric.group_material} selectedItemGroupCleaningCode={selectedFabric.group_cleaning_code} isPetAndKidFriendly={!!selectedFabric.pet_friendly} isSustainable={!!selectedFabric.sustainable_fabric} isGreenguardGold={!!selectedFabric.greenguard_fabric} selectedItemTier={selectedFabric.option_value_tier} selectedItemGrade={selectedFabric.option_value_grade} isPantoneFabric={selectedFabric.pantone_flag} materialComposition={selectedFabric.group_material_desc} />}

            <SanitizedHtml className="mx-0 my-6 text-sm" html={selectedFabric.option_description} />

            <div className="mb-12 flex flex-row gap-4 max-[767px]:flex-col max-[767px]:items-start">
              {selectedFabric?.option_value && <CtaLink to={`/swatches/swatch/${selectedFabric.option_value.trim().toLowerCase().replace(/ /g, '-')}/`} styling="solid-button" color="brand">
                  Shop This Color
                </CtaLink>}
              {selectedFabric?.option_value && <CtaLink to={`/customer-photos?materialOption=${encodeURIComponent(selectedFabric.option_value)}`} styling="link" color="brand" noLinkPadding={!!isMobile}>
                  View Customer Photos
                </CtaLink>}
            </div>

            {tabs.length > 0 && <TabPanel aria-label="Fabric detail tabs" items={tabs} selectedTab={currentSelection} onSelectTab={handleTabSelect} />}
          </div>
        </div>
      </SidebarModal>

      <PDPOrderSwatchesSidebar show={isOrderSwatchesSidebarOpen} onClose={() => {
      setIsOrderSwatchesSidebarOpen(false);
    }} closeSlidePanel={() => {
      setIsOrderSwatchesSidebarOpen(false);
    }} />
    </>;
};
SwatchesFabricDetailSidebarUI.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  reviews: PropTypes.array,
  rating: PropTypes.number,
  selectedFabric: PropTypes.shape({
    option_value: PropTypes.string,
    option_value_id: PropTypes.number,
    option_color: PropTypes.string,
    fabricURL: PropTypes.string,
    fabricFallbackURL: PropTypes.string,
    sliderImages: PropTypes.array,
    exclude_from_sale: PropTypes.bool,
    group_material: PropTypes.string,
    group_cleaning_code: PropTypes.string,
    pet_friendly: PropTypes.bool,
    sustainable_fabric: PropTypes.bool,
    greenguard_fabric: PropTypes.bool,
    option_value_tier: PropTypes.number,
    option_value_grade: PropTypes.number,
    pantone_flag: PropTypes.bool,
    option_description: PropTypes.string
  }),
  galleryType: PropTypes.string,
  mobileHideHeader: PropTypes.bool
};
export default withBrowserOnly(SwatchesFabricDetailSidebarUI);